// Inside the Even app (docs/flows/even-g2.yaml › One address, Josh 10-22): the web client stays the regular web app on
// the phone's screen and also drives the G2 glasses. Detection is a look at the Even bridge, never a wait. Outside the
// Even app nothing here runs and nothing is loaded.
//
// In this mode (app.js):
//  - the Macs are the ones the glasses paired (Even storage 'cody.g2.macs' — the addon's rooms()), each on its own
//    site with its own device id, and every connection signs with this device's key (rtc.js, mode "key"): the Even
//    WebView has no passkey, so a Mac that asks for one gets the "pair it again" card (macs.js `keyOnly`);
//  - Add a Mac is the typed code (the addon's pairing sheet — clients/g2's pair.js);
//  - the glasses add-on (clients/g2/src/addon.js, built to /g2/addon.js with the Even SDK) runs on the web client's
//    links: one connection per Mac, the glance over its wire channel.
import { openScanner } from '../qrscan/index.js'

/// The Even app's bridge is in this page.
export function evenBridge(w = globalThis) {
  try { return typeof w?.flutter_inappwebview?.callHandler === 'function' } catch { return false }
}

/// Where the add-on is: the site's /g2/, on whichever host this page came from.
export const addonURL = (origin = location.origin) => origin + '/g2/addon.js'

/// How long the app waits for the add-on before it runs without it (the page draws nothing until then).
export const LOAD_MS = 8000

/// The add-on, ready: the bridge and the glasses' Macs read. Null when it can't be had in LOAD_MS (logged): the web app
/// then runs as it does anywhere else.
export async function loadEven({ load = (url) => import(url), url = addonURL(), log = (l) => console.warn(l), wait = LOAD_MS } = {}) {
  let timer = null
  try {
    const late = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('no answer in ' + wait / 1000 + ' s')), wait) })
    return await Promise.race([load(url).then((mod) => mod.prepare()), late])
  } catch (e) { log('Even: the glasses add-on did not load — ' + (e?.message || e)); return null } finally { clearTimeout(timer) }
}

/// What the add-on calls back into: the web client's Macs (macs.js) by room. `onPaired(rec)`: a Mac paired with the
/// code (the app adopts it, or reloads with none before); `onForgot(room)`: forgotten from the pairing sheet.
export function hostFor(macs, { onPaired = () => {}, onForgot = () => {} } = {}) {
  return {
    mac: (room) => macs.get(room),
    retry: (room) => macs.retry(room),
    paired: (rec) => onPaired(rec),
    forgot: (room) => onForgot(room),
    // Scan QR for the pairing sheet (docs/flows/qr-scan.yaml › even): the web client's camera sheet, loaded only when
    // tapped; any of the Mac's QRs gives its code (null: Cancel / no camera — the code box stays)
    scan: () => openScanner().then((read) => read?.code ?? null),
  }
}

/// The connection to start with in this mode: the glasses' Macs, or none yet (the Connect button then pairs).
export const evenConnection = (even, site = location.origin) => (even.rooms().length ? { rooms: true, site } : null)
